import React from 'react';
import * as CSS from 'csstype';
import styled from 'styled-components';

/** 🐨 see if this should/could be imported from above reimported styled-components */
/** it is reimported so that we can leverage Theme Generics exported by styled-components */
import {CSSObject} from 'styled-components';
import {useTheme} from '@ibibo/build-theme';
import {Theme} from '@ibibo/build-theme/src/types';
// import {convertPixelToREM} from '../../theme/utils';

type ValidWrapperTextTagBag = 'div' | 'span' | 'small' | 'h4';

const Tag = styled.div(({styleProps}: {styleProps: CSSObject}) => ({
  ...styleProps,
}));

export type TextFieldExptProps = {
  /** Apply bold emphasis. */
  bold?: boolean;
  /** Render text inline instead of block */
  baseline?: boolean /** 🐨 make the name more obvious if it isn't. */;
  /** Render the text inline-block instead of block */
  inline?: boolean /** 🐨 make the name more obvious if it isn't. */;
  align?: CSS.Properties['textAlign'];
  /** Decrease font size to xsmall (from theme) */
  xsmall?: boolean;
  /** Decrease font size to small. */
  small?: boolean;
  /** Increase font size to small */
  large?: boolean;
  fontFamilyOverride?: CSS.Properties['fontFamily'];
  customFontSize?: '1' | '1.4' | '1.6' | '2' | '2.2' | '2.4';
  /** Disable text and white space wrapping. */
  noWrap?: boolean /**🐨 See if giving option for 'white-space' css property makes sense or is needed */;
  /** Preserve whitespace at the beginning and end of children. */
  preserveWhitespace?: boolean;
  /** Truncate the text with an ellipsis. */
  truncated?: boolean;
  uppercased?: boolean;
  strikethrough?: boolean;

  children?: React.ReactNode;
};

function generateStylePool(theme: Theme, props: TextFieldExptProps): CSSObject {
  const {font} = theme;
  const {align, fontFamilyOverride, customFontSize} = props;

  type ComponentPropsToCssPropsMap = Omit<
    {
      [P in keyof Required<TextFieldExptProps>]: CSSObject;
    },
    'children'
  >;

  const stylePropToCssPropsMap: ComponentPropsToCssPropsMap = {
    // fontFamily,
    baseline: {
      display: 'inline',
    },
    inline: {
      display: 'inline-block',
    },
    bold: {
      // fontWeight: font.weights.semibold,
      fontWeight: 'bold',
    },
    xsmall: {
      ...font.xsmall,
    },
    small: {
      ...font.small,
    },
    large: {
      ...font.large,
    },
    noWrap: {
      whiteSpace: 'nowrap',
    },
    preserveWhitespace: {
      whiteSpace: 'pre-wrap',
    },
    truncated: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '> div': {
        display: 'inline',
      },
    },
    uppercased: {
      textTransform: 'uppercase',
    },
    align: {
      textAlign: align,
    },
    fontFamilyOverride: {
      fontFamily: fontFamilyOverride,
    },
    customFontSize: {
      fontSize: customFontSize + 'rem',
    },
    strikethrough: {
      textDecoration: 'line-through',
    },
  };

  const stylesForEveryText: CSSObject = {
    // ...font.textReset,
    ...font.base,
  };

  /**
   * The reason why Object.keys do not infer correct types of keys –
   * https://stackoverflow.com/questions/55012174/why-doesnt-object-keys-return-a-keyof-type-in-typescript
   *
   * 🐨 see if second approach given in –
   * https://stackoverflow.com/questions/52856496/typescript-object-keys-return-string
   * can be used for ease of use of types by Object.keys
   */
  const keys = Object.keys(stylePropToCssPropsMap) as Array<keyof ComponentPropsToCssPropsMap>;
  const passedProps = keys.filter(propNameFromMap => propNameFromMap in props);

  function stylePropReducerCallBack<T = CSSObject>(
    acc: T,
    passedPropName: keyof ComponentPropsToCssPropsMap,
  ): T {
    return {
      ...acc,
      ...stylePropToCssPropsMap[passedPropName],
    };
  }

  const stylePropsDueToProps = passedProps.reduce(stylePropReducerCallBack, {} as CSSObject);

  const allStyleProps: CSSObject = {...stylesForEveryText, ...stylePropsDueToProps};

  return allStyleProps;
}

const generateStyledWrapperTagProps = (props: TextFieldExptProps, theme: Theme) => {
  const {inline, xsmall, large} = props;

  let as: ValidWrapperTextTagBag = 'div';

  const styleProps = generateStylePool(theme, props);

  if (inline) {
    as = 'span';
  } else if (xsmall) {
    as = 'small';
  } else if (large) {
    as = 'h4';
  }

  return {
    as,
    styleProps,
  };
};

const TextFieldExpt: React.FC<TextFieldExptProps> = props => {
  const {children} = props;

  const theme = useTheme();

  const {as, styleProps} = generateStyledWrapperTagProps(props, theme);

  return (
    <Tag as={as} styleProps={styleProps}>
      {children}
    </Tag>
  );
};

export default TextFieldExpt;
